// Formatting helpers.
//
// Amounts arrive as decimal STRINGS and are formatted for display only — they
// are never parsed into a JS number for arithmetic. Every sum shown on this
// page was computed in Postgres, where the type is numeric; the browser's job
// is to put commas in.

const YEN = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

/** Format a decimal string as yen. Rounds for display only. */
export function yen(value: string | null): string {
  if (value === null) return '—'
  const asNumber = Number(value)
  if (!Number.isFinite(asNumber)) return value
  return `¥${YEN.format(Math.round(asNumber))}`
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** 2026-03-01 → Mar 2026. */
export function monthLabel(month: string): string {
  const [year, m] = month.split('-')
  const index = Number(m) - 1
  return `${MONTH_NAMES[index] ?? m} ${year}`
}

/** 2026-03-28 → Mar 28, 2026. String-sliced like monthLabel — never through a
 * Date, whose UTC/local split can shift a day. */
export function dayLabel(date: string): string {
  const [year, m, d] = date.split('-')
  const index = Number(m) - 1
  return `${MONTH_NAMES[index] ?? m} ${Number(d)}, ${year}`
}

/** Human "3 hours ago", used by the data-source status. */
export function ago(iso: string | null): string {
  if (iso === null) return 'never'
  const elapsedMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(elapsedMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/** True when a decimal string is negative — drives the red/black styling. */
export function isNegative(value: string | null): boolean {
  return value !== null && value.trimStart().startsWith('-')
}

// Money on this page arrives as Postgres `Decimal` strings and must never be
// summed through a JS float — 0.1 + 0.2 ≠ 0.3, and a pivot total that drifts a
// yen from the DB is exactly the "which number is right" bug this project kills.
// The two helpers below add and compare decimal strings EXACTLY by scaling to
// integer minor units (2 dp, matching the DB's numeric(14,2)) and doing the
// arithmetic in BigInt. They return / consume the same decimal-string shape
// `yen()` formats, so display still goes through the one formatter.
const MINOR_SCALE = 2

function toMinorUnits(value: string): bigint {
  const trimmed = value.trim()
  const negative = trimmed.startsWith('-')
  const unsigned = negative || trimmed.startsWith('+') ? trimmed.slice(1) : trimmed
  const dot = unsigned.indexOf('.')
  const intPart = dot === -1 ? unsigned : unsigned.slice(0, dot)
  const fracPart = dot === -1 ? '' : unsigned.slice(dot + 1)
  const digits = `${intPart}${(fracPart + '00').slice(0, MINOR_SCALE)}`
  // Defensive: the API only ever sends numeric strings, but a non-numeric value
  // must not throw a BigInt parse error mid-render — treat it as zero.
  if (!/^\d+$/.test(digits)) return 0n
  const magnitude = BigInt(digits)
  return negative ? -magnitude : magnitude
}

function fromMinorUnits(units: bigint): string {
  const negative = units < 0n
  const abs = (negative ? -units : units).toString().padStart(MINOR_SCALE + 1, '0')
  const cut = abs.length - MINOR_SCALE
  return `${negative ? '-' : ''}${abs.slice(0, cut)}.${abs.slice(cut)}`
}

/** Sum decimal strings exactly (nulls skipped), returning a decimal string. */
export function sumYen(values: Array<string | null>): string {
  let units = 0n
  for (const value of values) {
    if (value !== null) units += toMinorUnits(value)
  }
  return fromMinorUnits(units)
}

/** a − b over decimal strings, exactly (BigInt minor units), as a decimal string. */
export function subtractYen(a: string, b: string): string {
  return fromMinorUnits(toMinorUnits(a) - toMinorUnits(b))
}

/** Compare two decimal strings numerically (ascending: a − b), float-free. */
export function compareYen(a: string, b: string): number {
  const ua = toMinorUnits(a)
  const ub = toMinorUnits(b)
  return ua < ub ? -1 : ua > ub ? 1 : 0
}
